import React, { useMemo, useState } from "react";
import { Alert, Box, Button, Divider, Drawer, IconButton, Stack, Typography, useTheme } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import RestoreFromTrashIcon from "@mui/icons-material/RestoreFromTrash";
import { useQuery } from "@tanstack/react-query";
import { FormattedMessage, useIntl } from "react-intl";
import { fetchUsers } from "../../../fetchers/user";
import { useStockMovements } from "../../../hooks/useStockMovements";
import { HideIfNoRole } from "../../../auth/authUi";
import { CMMS_MANAGER_ROLES } from "../../cmms/cmmsRoles";
import cmmsMessages from "../../cmms/cmmsMessages";
import { formatQuantity, formatTimestamp } from "../../cmms/cmmsFormat";
import { CmmsEmptyState, CmmsErrorState, CmmsListSkeleton } from "../../cmms/components/CmmsStates";
import {
  cmmsTypeScale,
  getAssetHierarchyPalette,
  getContainedButtonSx,
  getOutlinedButtonSx,
  getPanelTitleSx,
  getStatLabelSx,
} from "../../cmms/assetHierarchyStyles";
import messages from "../messages";
import {
  StockMovementType,
  getStockMovementQuantityColor,
  getStockMovementTypeLabel,
} from "../stockMovements";
import StockMovementFormDialog from "./StockMovementFormDialog";
import StocktakeDialog from "./StocktakeDialog";

/*
 * Everything about one part's stock, in a drawer off the register.
 *
 * Shaped after features/tools ToolServiceHistoryDrawer: always mounted and opened on the part,
 * so it can animate out rather than vanish, while `enabled` on both reads keeps a drawer nobody
 * opened from costing a request.
 *
 * The balance and the reorder point come from the row the grid already holds, not a second
 * read. They are the same two numbers the list is showing, and fetching them again here would
 * create a moment where the drawer and the row behind it disagree.
 *
 * The ledger has no role gate: reading movements is Cmms_Field, because a technician who books
 * a part onto a job has every reason to see what has happened to it. The three buttons are
 * Cmms_Manager, which is what the endpoints require.
 */
export default function SparePartStockDrawer({ sparePart, companyKey, onClose, onRestore }) {
  const theme = useTheme();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);
  const intl = useIntl();

  const open = Boolean(sparePart);
  const history = useStockMovements(sparePart?.id ?? null);

  const [movementType, setMovementType] = useState(null);
  const [stocktakeOpen, setStocktakeOpen] = useState(false);

  // Same key and staleTime as the tool drawer, so whichever opens first warms the other.
  const usersQuery = useQuery(["users"], () => fetchUsers(), { enabled: open, staleTime: 300000 });

  const userNameById = useMemo(() => {
    const map = new Map();
    (usersQuery.data ?? []).forEach((user) => {
      map.set(user.id, user.name ?? user.username ?? `#${user.id}`);
    });
    return map;
  }, [usersQuery.data]);

  const rows = history.movements;

  const renderLedger = () => {
    if (history.isLoading) return <CmmsListSkeleton rows={4} height={40} />;

    if (history.isError) {
      return (
        <CmmsErrorState
          error={history.error}
          fallback={intl.formatMessage(messages.stockLedgerLoadFailed)}
          onRetry={() => history.refetch()}
          isRetrying={history.isFetching}
        />
      );
    }

    if (rows.length === 0) {
      return (
        <CmmsEmptyState
          icon={<Inventory2Icon />}
          title={intl.formatMessage(messages.stockLedgerEmpty)}
          // The hint says where rows come from, including the one place they do not: what a
          // work order uses is booked on the order, and a reader who does not know that will
          // read an empty ledger as a broken one.
          hint={intl.formatMessage(messages.stockLedgerEmptyHint)}
          dense
        />
      );
    }

    return (
      <Stack divider={<Divider flexItem />} spacing={0}>
        {rows.map((movement) => (
            <Box key={movement.id} sx={{ py: 1.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {getStockMovementTypeLabel(movement.movementType, intl)}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 700, color: getStockMovementQuantityColor(movement.quantity) }}
                >
                  {movement.quantity > 0 ? "+" : ""}{formatQuantity(intl, movement.quantity, 2)}
                </Typography>
              </Stack>
              <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.25 }}>
                {[
                  formatTimestamp(intl, movement.movedAt),
                  movement.movedByUserId
                    ? intl.formatMessage(messages.stockMovedBy, {
                        user: userNameById.get(movement.movedByUserId) ?? `#${movement.movedByUserId}`,
                      })
                    : null,
                ].filter(Boolean).join(" · ")}
              </Typography>
              {/*
                * A stocktake row shows its difference and nothing else.
                *
                * The counted quantity is not shown because it cannot be worked out here: it is
                * the balance immediately after the row, and that needs the consumption
                * timeline, which lives on the work orders rather than in this ledger. The
                * server deliberately does not store it — a second number about the same thing is what a
                * derived balance exists to avoid — so the difference is the honest record, and
                * inventing the count from movements alone would be wrong for any part a work
                * order has ever booked.
                */}
              {movement.reason &&
                <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                  {movement.reason}
                </Typography>
              }
            </Box>
        ))}
      </Stack>
    );
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{ sx: { width: { xs: "100%", sm: 520 }, backgroundColor: cmmsColors.surface } }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <Box sx={{ p: 2.5, borderBottom: `1px solid ${cmmsColors.border}` }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
              <Box>
                <Typography variant={cmmsTypeScale.panelTitle} sx={getPanelTitleSx(cmmsColors)}>
                  {intl.formatMessage(messages.stockTitle)}
                </Typography>
                <Typography variant="caption" sx={{ color: cmmsColors.mutedText }}>
                  {sparePart?.partNumber} · {sparePart?.name}
                </Typography>
              </Box>
              <IconButton onClick={onClose} aria-label={intl.formatMessage(cmmsMessages.close)}>
                <CloseIcon />
              </IconButton>
            </Stack>

            {/*
              * The three figures stay on h4 — bigger than the drawer's own h5 title, which is the
              * point: they are what the drawer was opened to read. That used to collide with h4
              * meaning "page title" on five pages; FE-15.7 moved the page title to h3, so the
              * variant now means one thing here and another there without ambiguity.
              *
              * Their captions go through getStatLabelSx like every other statistic label. They
              * were a third label style — caption, no weight, no uppercase — until now.
              *
              * Max joined min here rather than min standing alone under a label that had just
              * become half of a pair. The two are what the balance beside them is judged against;
              * showing one of them and not the other is the drawer answering half a question.
              *
              * It wraps now that there are three. On a phone the drawer is the whole screen
              * width, and "1 250,00 st" beside two levels at h4 is more than that fits; the third
              * figure drops to a second line instead of the row overflowing its own panel.
              */}
            <Stack direction="row" spacing={4} useFlexGap flexWrap="wrap" sx={{ mt: 2, rowGap: 2 }}>
              <Box>
                <Typography variant={cmmsTypeScale.statLabel} sx={getStatLabelSx(cmmsColors)}>
                  <FormattedMessage {...messages.stockOnHand} />
                </Typography>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 700, color: sparePart?.isBelowReorderPoint ? "warning.main" : cmmsColors.text }}
                >
                  {formatQuantity(intl, sparePart?.quantityOnHand ?? 0, 2)} {sparePart?.unit}
                </Typography>
              </Box>
              <Box>
                <Typography variant={cmmsTypeScale.statLabel} sx={getStatLabelSx(cmmsColors)}>
                  <FormattedMessage {...messages.stockReorderPoint} />
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: cmmsColors.mutedText }}>
                  {sparePart?.reorderPoint === null || sparePart?.reorderPoint === undefined
                    ? intl.formatMessage(messages.stockNoReorderPoint)
                    : formatQuantity(intl, sparePart.reorderPoint, 2)}
                </Typography>
              </Box>
              <Box>
                <Typography variant={cmmsTypeScale.statLabel} sx={getStatLabelSx(cmmsColors)}>
                  <FormattedMessage {...messages.stockMaxStockLevel} />
                </Typography>
                {/*
                  * "Not set" is shared with min above. One string for an absent level, not two
                  * identical ones for translators to keep in step.
                  */}
                <Typography variant="h4" sx={{ fontWeight: 700, color: cmmsColors.mutedText }}>
                  {sparePart?.maxStockLevel === null || sparePart?.maxStockLevel === undefined
                    ? intl.formatMessage(messages.stockNoReorderPoint)
                    : formatQuantity(intl, sparePart.maxStockLevel, 2)}
                </Typography>
              </Box>
            </Stack>

            {/*
              * The way out sits next to the problem: the restore button lives in this alert.
              *
              * This alert is where a stranded balance is discovered: a retired part still showing
              * 7 on hand, with all three action buttons gone. Without a restore there would be
              * nothing to offer here, and the number would be unreachable — movements are refused
              * against an inactive part, so it could not even be written off.
              */}
            {sparePart && !sparePart.isActive &&
              <Alert
                severity="info"
                sx={{ mt: 2 }}
                action={
                  <HideIfNoRole anyOf={CMMS_MANAGER_ROLES}>
                    <Button
                      size="small"
                      color="inherit"
                      startIcon={<RestoreFromTrashIcon />}
                      onClick={() => onRestore?.(sparePart)}
                    >
                      <FormattedMessage {...messages.actionRestore} />
                    </Button>
                  </HideIfNoRole>
                }
              >
                <FormattedMessage {...messages.stockRetired} />
              </Alert>
            }

            {sparePart?.isActive &&
              <HideIfNoRole anyOf={CMMS_MANAGER_ROLES}>
                {/*
                  * The module's own button styling, not MUI's default. This drawer's paper is
                  * cmmsColors.surface, which is the very colour palette.primary.main resolves to
                  * — so a bare contained button paints itself the shade of the panel behind it,
                  * and an outlined one borrows the same for its border and its text.
                  */}
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 2, gap: 1 }}>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setMovementType(StockMovementType.Receipt)}
                    sx={getContainedButtonSx(cmmsColors)}
                  >
                    <FormattedMessage {...messages.stockActionReceipt} />
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<SwapVertIcon />}
                    onClick={() => setMovementType(StockMovementType.Adjustment)}
                    sx={getOutlinedButtonSx(cmmsColors)}
                  >
                    <FormattedMessage {...messages.stockActionAdjust} />
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<FactCheckIcon />}
                    onClick={() => setStocktakeOpen(true)}
                    sx={getOutlinedButtonSx(cmmsColors)}
                  >
                    <FormattedMessage {...messages.stockActionStocktake} />
                  </Button>
                </Stack>
              </HideIfNoRole>
            }
          </Box>

          <Box sx={{ flex: 1, overflow: "auto", px: 2.5, py: 1 }}>
            <Typography variant="overline" sx={{ color: cmmsColors.subtleText, fontWeight: 700 }}>
              <FormattedMessage {...messages.stockLedgerTitle} />
            </Typography>
            {renderLedger()}
          </Box>
        </Box>
      </Drawer>

      {sparePart &&
        <>
          <StockMovementFormDialog
            open={movementType !== null}
            initialType={movementType ?? StockMovementType.Receipt}
            sparePart={sparePart}
            companyKey={companyKey}
            onClose={() => setMovementType(null)}
          />
          <StocktakeDialog
            open={stocktakeOpen}
            sparePart={sparePart}
            companyKey={companyKey}
            onClose={() => setStocktakeOpen(false)}
          />
        </>
      }
    </>
  );
}
